import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getSubscriptionTypeFromToken } from '../utils/jwt';
import {
  formatVnd,
  getOrderedPackageEntries,
  summarizeIncludedFeatures,
} from '../utils/packageCatalog';
import {
  normalizeBusinessType,
  openingHoursFromText,
} from '../utils/siteBusinessFields';
import PasteReviews from './PasteReviews';
import './ConfirmSite.css';

const MONETISATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'standard', label: 'Standard' },
  { value: 'full', label: 'Full' },
];

function normalizeMonetisationMode(value, fallback = 'off') {
  const normalized = String(value || '').trim().toLowerCase();
  if (MONETISATION_MODE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
}

function normalizeDestinationField(value) {
  return String(value || '').trim().toLowerCase();
}

function safeJsonParseValue(value, fallback = null) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
}

function formatJsonCsvValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const parsed = safeJsonParseValue(value, null);
    return parsed == null ? value : JSON.stringify(parsed);
  }
  return JSON.stringify(value);
}

function formatOpeningHoursCsvValue(site) {
  const openingHours = safeJsonParseValue(site?.opening_hours_json, null);
  const weekdayDescriptions = Array.isArray(openingHours?.weekdayDescriptions)
    ? openingHours.weekdayDescriptions.map((line) => String(line || '').trim()).filter(Boolean)
    : [];
  if (weekdayDescriptions.length) return weekdayDescriptions.join('\n');
  return String(site?.opening_hours || '').trim();
}

function escapeCsvCell(value) {
  const stringValue = String(value == null ? '' : value);
  return `"${stringValue.replace(/"/g, '""')}"`;
}

function downloadCsvFile(filename, columns, rows) {
  const headerRow = columns.map((column) => escapeCsvCell(column.label)).join(',');
  const bodyRows = rows.map((row) =>
    columns.map((column) => escapeCsvCell(column.getValue(row))).join(',')
  );
  const blob = new Blob([[headerRow, ...bodyRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const SITE_INFO_EXPORT_COLUMNS = [
  { label: 'Site ID', getValue: (site) => site.id || '' },
  { label: 'Site Name', getValue: (site) => site.name || '' },
  { label: 'Site Type', getValue: (site) => site.site_type || '' },
  { label: 'Address', getValue: (site) => site.address || '' },
  { label: 'Phone', getValue: (site) => site.phone || '' },
  { label: 'Contact Email', getValue: (site) => site.contact_email || site.contactEmail || '' },
  { label: 'Website', getValue: (site) => site.website || '' },
  { label: 'Place ID', getValue: (site) => site.place_id || '' },
  { label: 'Google Maps URL', getValue: (site) => site.google_url || '' },
  { label: 'Latitude', getValue: (site) => site.lat ?? '' },
  { label: 'Longitude', getValue: (site) => site.lng ?? '' },
  { label: 'Business Type', getValue: (site) => site.business_type || '' },
  { label: 'Business Status', getValue: (site) => site.business_status || '' },
  { label: 'Primary Type', getValue: (site) => site.primary_type || '' },
  {
    label: 'Primary Type Display Name',
    getValue: (site) => site.primary_type_display_name || '',
  },
  {
    label: 'YouTube Region Code',
    getValue: (site) => site.youtube_region_code || '',
  },
  { label: 'Public Rating', getValue: (site) => site.rating ?? '' },
  { label: 'Public Review Count', getValue: (site) => site.ratings_total ?? '' },
  {
    label: 'UTC Offset Minutes',
    getValue: (site) => site.utc_offset_minutes ?? '',
  },
  {
    label: 'Regular Hours Of Operation',
    getValue: (site) => formatOpeningHoursCsvValue(site),
  },
  {
    label: 'Current Opening Hours JSON',
    getValue: (site) => formatJsonCsvValue(site.current_opening_hours_json),
  },
  {
    label: 'Special Opening Hours JSON',
    getValue: (site) => formatJsonCsvValue(site.special_opening_hours_json),
  },
  {
    label: 'Place Extras JSON',
    getValue: (site) => formatJsonCsvValue(site.place_extras_json),
  },
  { label: 'Wi-Fi SSID', getValue: (site) => site.wifi_ssid || '' },
  { label: 'Wi-Fi Password', getValue: (site) => site.wifi_password || '' },
  { label: 'Facebook Page URL', getValue: (site) => site.facebook_url || '' },
  { label: 'Instagram URL', getValue: (site) => site.instagram_url || '' },
  { label: 'Twitter URL', getValue: (site) => site.twitter_url || '' },
  { label: 'Pinterest URL', getValue: (site) => site.pinterest_url || '' },
  { label: 'TikTok URL', getValue: (site) => site.tiktok_url || '' },
  { label: 'YouTube URL', getValue: (site) => site.youtube_url || '' },
  { label: 'Spotify URL', getValue: (site) => site.spotify_url || '' },
  { label: 'SoundCloud URL', getValue: (site) => site.soundcloud_url || '' },
  { label: 'Mixcloud URL', getValue: (site) => site.mixcloud_url || '' },
  { label: 'Apple Music URL', getValue: (site) => site.apple_music_url || '' },
  { label: 'TripAdvisor URL', getValue: (site) => site.tripadvisor_url || '' },
  { label: 'Google Review URL', getValue: (site) => site.google_review_url || '' },
  { label: 'Menu URL', getValue: (site) => site.menu_url || '' },
  { label: 'Business Description', getValue: (site) => site.business_description || '' },
  {
    label: 'Social Media Stats',
    getValue: (site) => formatJsonCsvValue(site.social_media_stats),
  },
  { label: 'Photos JSON', getValue: (site) => formatJsonCsvValue(site.photos_json) },
  { label: 'Details Fetched At', getValue: (site) => site.details_fetched_at || '' },
  { label: 'Reviews Fetched At', getValue: (site) => site.reviews_fetched_at || '' },
];

function PlacePhoto({ name, maxWidth = 200, className = '', alt = '' }) {
  const [src, setSrc] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !name) {
      setSrc('');
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = '';

    const load = async () => {
      try {
        const res = await fetch(
          `/api/places/photo?name=${encodeURIComponent(name)}&maxWidth=${encodeURIComponent(maxWidth)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!res.ok) {
          throw new Error(`Photo request failed (${res.status})`);
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSrc('');
        }
      }
    };

    load();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [maxWidth, name]);

  if (!src) {
    return <div className={`${className} bg-gray-200`} />;
  }

  return <img src={src} alt={alt} className={className} />;
}

function joinClassNames(...values) {
  return values.filter(Boolean).join(' ');
}

function SummaryStat({ label, value, helper = '' }) {
  return (
    <div className="confirm-site-stat">
      <div className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold text-white">
        {value}
      </div>
      {helper ? (
        <div className="mt-2 text-sm text-slate-400">
          {helper}
        </div>
      ) : null}
    </div>
  );
}

function DisclosureSection({
  title,
  description,
  summary,
  defaultOpen = false,
  children,
}) {
  return (
    <details open={defaultOpen} className="confirm-site-section">
      <summary>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-lg font-semibold text-white">{title}</div>
            {description ? (
              <div className="mt-1 text-sm text-slate-400">{description}</div>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {summary ? <span className="confirm-site-chip">{summary}</span> : null}
            <span className="confirm-site-chip">Toggle</span>
          </div>
        </div>
      </summary>
      <div className="confirm-site-section-body">
        {children}
      </div>
    </details>
  );
}

export default function ConfirmSite({
  siteId: propSiteId,
  placeId: initialPlaceId,
  onClose,
  match,
  area,
  sharedDestinations = [],
  entityMode = 'venue',
  refreshSitesAreasDevices,
  isAdmin = false,
  onOpenDestinationSetup = null,
  mode = 'details',
}) {
  const [siteId, setSiteId] = useState(propSiteId || match?.params?.siteId);
  const [placeId, setPlaceId] = useState(initialPlaceId || match?.params?.placeId || 'existing');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editable, setEditable] = useState(null);
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPass, setWifiPass] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [pinterestUrl, setPinterestUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [mixcloudUrl, setMixcloudUrl] = useState('');
  const [appleMusicUrl, setAppleMusicUrl] = useState('');
  const [tripadvisorUrl, setTripadvisorUrl] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [menuUrl, setMenuUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [defaultMonetisationMode, setDefaultMonetisationMode] = useState('off');
  const logoFileInputRef = useRef(null);
  const [siteMaps, setSiteMaps] = useState([]);
  const [mapsLoading, setMapsLoading] = useState(false);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [mapNameDraft, setMapNameDraft] = useState('');
  const [mapPlaques, setMapPlaques] = useState([]);
  const [activeMarkerPlaqueId, setActiveMarkerPlaqueId] = useState(null);
  const [labelScale, setLabelScale] = useState(1);
  const [labelOpacity, setLabelOpacity] = useState(1);
  const mapFileInputRef = useRef(null);
  const mapPreviewRef = useRef(null);
  const [businessDescription, setBusinessDescription] = useState('');
  const [socialStats, setSocialStats] = useState({});
  const [packages, setPackages] = useState([]);
  const [reviewSummary, setReviewSummary] = useState('');
  const [reviewQuotes, setReviewQuotes] = useState([]);
  const [marketingKit, setMarketingKit] = useState('');
  const [analysingReviews, setAnalysingReviews] = useState(false);
  const [reviewAnalysisMessage, setReviewAnalysisMessage] = useState('');
  const [progress, setProgress] = useState('');
  const [action, setAction] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [packageCatalog, setPackageCatalog] = useState({});
  const [packageCatalogError, setPackageCatalogError] = useState('');
  const [selectedSitePackageKey, setSelectedSitePackageKey] = useState('standard');
  const [destinationName, setDestinationName] = useState('');
  const [destinationCountryCode, setDestinationCountryCode] = useState('');
  const autoSaveInProgress = useRef(false);
  const isDestinationMode = entityMode === 'destination';
  const siteLabel = isDestinationMode ? 'Destination' : 'Site';
  const areaLabel = isDestinationMode ? 'Activity' : 'Area';
  const detailTitle = isDestinationMode ? 'Destination Details' : 'Site Details';
  const subscriptionType = String(
    getSubscriptionTypeFromToken(localStorage.getItem('token')) || 'free'
  ).trim().toLowerCase();
  const monetisationIsEnforced = subscriptionType === 'free';
  const packageCards = getOrderedPackageEntries(packageCatalog);

  const safeJsonParse = (value, fallback = null) => {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };

  const humanizeBusinessType = (type) => {
    const raw = String(type || '').trim();
    if (!raw) return '';
    return raw.replace(/_/g, ' ');
  };

  const articleFor = (phrase) => {
    const first = String(phrase || '').trim().charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(first) ? 'an' : 'a';
  };

  const buildDefaultBusinessDescription = (siteData) => {
    const name = String(siteData?.name || '').trim();
    const businessType = String(siteData?.business_type || '').trim();
    if (!businessType) return '';

    const phrases = {
      restaurant: 'restaurant serving food and drinks',
      bar: 'bar serving drinks and cocktails',
      cafe: 'café serving coffee and light bites',
      bakery: 'bakery offering fresh baked goods',
      dentist: 'dental practice providing oral health care',
      doctor: 'medical clinic providing health care services',
      hospital: 'hospital providing medical services',
      pharmacy: 'pharmacy dispensing prescriptions and health products',
      gym: 'fitness center offering workouts and training',
      hair_care: 'hair salon offering cuts, color, and styling',
      beauty_salon: 'beauty salon offering cosmetic services',
      spa: 'spa offering wellness and relaxation services',
      lodging: 'hotel providing lodging and accommodations',
      car_dealer: 'car dealership selling new and used vehicles',
      car_repair: 'auto repair shop providing vehicle service and maintenance',
      real_estate_agency: 'real estate agency helping people buy and sell property',
    };

    const phrase = phrases[businessType] || humanizeBusinessType(businessType) || 'business';
    const article = articleFor(phrase);
    if (name) return `${name} is ${article} ${phrase}.`;
    return `${article.charAt(0).toUpperCase()}${article.slice(1)} ${phrase}.`;
  };

  const getOpeningHoursLines = (openingHoursJson) => {
    const lines = openingHoursJson?.weekdayDescriptions;
    if (!Array.isArray(lines)) return [];
    return lines.map((line) => String(line || '').trim()).filter(Boolean);
  };

  const openingHoursToText = (openingHoursJson) =>
    getOpeningHoursLines(openingHoursJson).join('\n');

  const getUrlToOpen = (rawUrl) => {
    const trimmed = String(rawUrl || '').trim();
    if (!trimmed) return null;
    try {
      return new URL(trimmed).toString();
    } catch (e) {
      // ignore
    }
    try {
      return new URL(`https://${trimmed}`).toString();
    } catch (e) {
      // ignore
    }
    return null;
  };

  const handleCheckUrl = (rawUrl) => {
    const urlToOpen = getUrlToOpen(rawUrl);
    if (!urlToOpen) {
      alert('Please enter a valid URL first.');
      return;
    }
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  const openGoogleSearch = (queryText) => {
    const q = String(queryText || '').trim();
    if (!q) return;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer');
  };

  const getBusinessQueryBase = () => {
    const name = editable?.siteData?.name || '';
    const address = editable?.siteData?.address || '';
    return `${name} ${address}`.trim();
  };

  const handleUploadLogoClick = () => {
    if (!logoFileInputRef.current) return;
    logoFileInputRef.current.value = '';
    logoFileInputRef.current.click();
  };

  const handleLogoFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!siteId) {
      alert('No site selected.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in to upload a logo.');
      return;
    }

    setProgress('Uploading logo...');
    try {
      const presignRes = await axios.get(`/api/sites/${siteId}/logo/presign`, {
        params: { filename: file.name },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { url, key } = presignRes.data || {};
      if (!url || !key) {
        throw new Error('Invalid presign response');
      }

      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }

      const finalizeRes = await axios.post(
        `/api/sites/${siteId}/logo/finalize`,
        { key, filename: file.name, fileType: file.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newLogoUrl = finalizeRes.data?.logoUrl || '';
      if (!newLogoUrl) {
        throw new Error('Logo upload finalized but no URL returned');
      }
      setLogoUrl(newLogoUrl);
      setProgress('Logo uploaded.');
      setTimeout(() => setProgress(''), 1500);
    } catch (err) {
      console.error('Logo upload failed', err);
      setProgress('');
      alert('Failed to upload logo.');
    }
  };

  const loadSiteMaps = useCallback(async () => {
    if (!siteId) {
      setSiteMaps([]);
      setSelectedMapId(null);
      setMapNameDraft('');
      return;
    }
    const token = localStorage.getItem('token');
    setMapsLoading(true);
    try {
      const res = await axios.get('/api/site-maps', {
        params: { siteId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const maps = Array.isArray(res.data?.maps) ? res.data.maps : [];
      setSiteMaps(maps);
      if (maps.length) {
        const nextId = selectedMapId && maps.some((map) => String(map.id) === String(selectedMapId))
          ? selectedMapId
          : maps[0].id;
        setSelectedMapId(nextId);
      } else {
        setSelectedMapId(null);
        setMapNameDraft('');
      }
    } catch (err) {
      console.error('Failed to load site maps', err);
      setSiteMaps([]);
      setSelectedMapId(null);
    } finally {
      setMapsLoading(false);
    }
  }, [siteId, selectedMapId]);

  useEffect(() => {
    loadSiteMaps();
  }, [loadSiteMaps]);

  useEffect(() => {
    if (action !== 'addSite' || isDestinationMode || packageCards.length) return;
    let ignore = false;

    const loadPackages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/entitlements/packages', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!ignore) {
          setPackageCatalog(
            res.data?.packageCatalog && typeof res.data.packageCatalog === 'object' && !Array.isArray(res.data.packageCatalog)
              ? res.data.packageCatalog
              : {}
          );
          setPackageCatalogError('');
        }
      } catch (error) {
        if (!ignore) {
          setPackageCatalog({});
          setPackageCatalogError('Failed to load package options.');
        }
      }
    };

    loadPackages();
    return () => {
      ignore = true;
    };
  }, [action, isDestinationMode, packageCards.length]);

  useEffect(() => {
    if (!selectedMapId) {
      setMapNameDraft('');
      setLabelScale(1);
      setLabelOpacity(1);
      return;
    }
    const selected = siteMaps.find((map) => String(map.id) === String(selectedMapId));
    setMapNameDraft(selected?.name || '');
    setLabelScale(typeof selected?.labelScale === 'number' ? selected.labelScale : 1);
    setLabelOpacity(typeof selected?.labelOpacity === 'number' ? selected.labelOpacity : 1);
  }, [selectedMapId, siteMaps]);

  const handleUploadMapClick = () => {
    if (!mapFileInputRef.current) return;
    mapFileInputRef.current.value = '';
    mapFileInputRef.current.click();
  };

  const handleMapFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!siteId) {
      alert('No site selected.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in to upload a map.');
      return;
    }
    setProgress('Uploading map...');
    try {
      const presignRes = await axios.get('/api/site-maps/presign', {
        params: { siteId, filename: file.name },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { url, key } = presignRes.data || {};
      if (!url || !key) {
        throw new Error('Invalid presign response');
      }
      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }

      const finalizeRes = await axios.post(
        '/api/site-maps/finalize',
        { siteId, key, filename: file.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = finalizeRes.data?.map || null;
      if (!created?.id) {
        throw new Error('Map upload finalized but no map returned');
      }
      setProgress('Map uploaded.');
      setTimeout(() => setProgress(''), 1500);
      await loadSiteMaps();
      setSelectedMapId(created.id);
    } catch (err) {
      console.error('Map upload failed', err);
      setProgress('');
      alert('Failed to upload map.');
    }
  };

  const handleSaveMapSettings = async () => {
    if (!selectedMapId) return;
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        `/api/site-maps/${selectedMapId}`,
        {
          name: mapNameDraft || null,
          labelScale,
          labelOpacity,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSiteMaps();
    } catch (err) {
      console.error('Failed to update map name', err);
      alert('Failed to update map settings.');
    }
  };

  const loadMapPlaques = useCallback(async () => {
    if (!siteId) {
      setMapPlaques([]);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`/api/plaques?siteId=${encodeURIComponent(siteId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data?.plaques) ? res.data.plaques : [];
      setMapPlaques(rows);
    } catch (err) {
      console.error('Failed to load plaques for map', err);
      setMapPlaques([]);
    }
  }, [siteId]);

  useEffect(() => {
    loadMapPlaques();
  }, [loadMapPlaques]);

  const updatePlaqueMapSettings = async (plaqueId, payload) => {
    const token = localStorage.getItem('token');
    await axios.put(`/api/plaques/${plaqueId}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await loadMapPlaques();
  };

  const handleMapClick = async (event) => {
    if (!activeMarkerPlaqueId || !selectedMapId || !mapPreviewRef.current) return;
    const rect = mapPreviewRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    try {
      await updatePlaqueMapSettings(activeMarkerPlaqueId, {
        mapId: Number(selectedMapId),
        mapMarkerX: Math.min(1, Math.max(0, x)),
        mapMarkerY: Math.min(1, Math.max(0, y)),
        mapMarkerEnabled: true,
      });
    } catch (err) {
      console.error('Failed to save marker position', err);
      alert('Failed to save marker position.');
    }
  };

  const handleDeleteMap = async () => {
    if (!selectedMapId) return;
    const ok = window.confirm('Delete this map? This cannot be undone.');
    if (!ok) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/site-maps/${selectedMapId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadSiteMaps();
    } catch (err) {
      console.error('Failed to delete map', err);
      alert('Failed to delete map.');
    }
  };

  const plaquesOnSelectedMap = mapPlaques.filter(
    (plaque) => selectedMapId && String(plaque.map_id) === String(selectedMapId)
  );
  const legendPlaques = plaquesOnSelectedMap
    .filter((plaque) => plaque.map_include_in_legend)
    .sort((a, b) => {
      const aOrder = a.map_legend_order ?? Number.POSITIVE_INFINITY;
      const bOrder = b.map_legend_order ?? Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.title || '').localeCompare(String(b.title || ''));
    })
    .map((plaque, index) => ({ ...plaque, legendNumber: index + 1 }));
  const legendNumberById = new Map(legendPlaques.map((plaque) => [plaque.id, plaque.legendNumber]));

  const applySiteRecordToState = useCallback((site) => {
    const editableData = {
      siteData: {
        name: site.name || '',
        site_type: site.site_type || '',
        country_code: site.country_code || '',
        youtube_region_code: site.youtube_region_code || site.country_code || '',
        destination_slug: site.destination_slug || '',
        address: site.address || '',
        phone: site.phone || '',
        website: site.website || '',
        place_id: site.place_id || '',
        google_url: site.google_url || '',
        lat: site.lat,
        lng: site.lng,
        rating: site.rating,
        ratings_total: site.ratings_total,
        business_type: site.business_type,
        business_status: site.business_status || '',
        primary_type: site.primary_type || '',
        primary_type_display_name: site.primary_type_display_name || '',
        utc_offset_minutes: site.utc_offset_minutes ?? null,
        opening_hours_json: safeJsonParse(site.opening_hours_json, null),
        current_opening_hours_json: safeJsonParse(site.current_opening_hours_json, null),
        special_opening_hours_json: safeJsonParse(site.special_opening_hours_json, null),
        place_extras_json: safeJsonParse(site.place_extras_json, null),
      },
      reviewsJson: site.reviews_json ? JSON.parse(site.reviews_json) : [],
      photosJson: site.photos_json ? JSON.parse(site.photos_json) : [],
    };
    setDraft(editableData);
    setEditable(editableData);
    setWifiSsid(site.wifi_ssid || '');
    setWifiPass(site.wifi_password || '');
    setContactEmail(site.contact_email || site.contactEmail || '');
    setFacebookUrl(site.facebook_url || '');
    setInstagramUrl(site.instagram_url || '');
    setTwitterUrl(site.twitter_url || '');
    setPinterestUrl(site.pinterest_url || '');
    setTiktokUrl(site.tiktok_url || '');
    setYoutubeUrl(site.youtube_url || '');
    setSpotifyUrl(site.spotify_url || '');
    setSoundcloudUrl(site.soundcloud_url || '');
    setMixcloudUrl(site.mixcloud_url || '');
    setAppleMusicUrl(site.apple_music_url || '');
    setTripadvisorUrl(site.tripadvisor_url || '');
    setGoogleReviewUrl(site.google_review_url || '');
    setMenuUrl(site.menu_url || '');
    setLogoUrl(site.logo_url || '');
    setDefaultMonetisationMode(
      normalizeMonetisationMode(site.defaultMonetisationMode || site.default_monetisation_mode || 'off', 'off')
    );
    const savedDescription = String(site.business_description || '').trim();
    setBusinessDescription(
      savedDescription || buildDefaultBusinessDescription(editableData.siteData)
    );
    setSocialStats(
      site.social_media_stats ? JSON.parse(site.social_media_stats) : {}
    );
    setPackages(
      site.packages_json ? JSON.parse(site.packages_json) : []
    );
    setReviewSummary(site.review_summary || '');
    setReviewQuotes(
      site.review_quotes ? JSON.parse(site.review_quotes) : []
    );
    setMarketingKit(site.marketing_kit || '');
  }, []);

  const loadFromDb = useCallback(async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get('/api/sites', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const site = res.data.sites.find(
      (s) => String(s.id) === String(siteId)
    );
    if (site) applySiteRecordToState(site);
  }, [applySiteRecordToState, siteId]);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (placeId === 'manual') {
      const empty = { siteData: {}, reviewsJson: [], photosJson: [] };
      setDraft(empty);
      setEditable(empty);
      setReviewSummary('');
      setReviewQuotes([]);
      setMarketingKit('');
      setWifiSsid('');
      setWifiPass('');
      setContactEmail('');
      setFacebookUrl('');
      setInstagramUrl('');
      setTwitterUrl('');
      setPinterestUrl('');
      setTripadvisorUrl('');
      setGoogleReviewUrl('');
      setMenuUrl('');
      setLogoUrl('');
      setDefaultMonetisationMode('off');
      setBusinessDescription('');
      setSocialStats({});
      setPackages([]);
      setProgress('');
      return;
    }
    if (placeId === 'existing') {
      await loadFromDb();
      setProgress('');
      return;
    }
    setProgress('Searching Google Maps');
    const res = await axios.post(
      `/api/sites/${siteId}/import-business`,
      { placeId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setDraft(res.data);
    setEditable(res.data);
    setReviewSummary('');
    setReviewQuotes([]);
    setMarketingKit('');
    const importedDescription = String(res.data?.business_description || '').trim();
    setBusinessDescription(
      importedDescription || buildDefaultBusinessDescription(res.data?.siteData)
    );

    if (res.data.siteData?.name) {
      await axios.put(
        `/api/sites/${siteId}`,
        { name: res.data.siteData.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (refreshSitesAreasDevices)
        await refreshSitesAreasDevices(siteId, area?.id, true);
    }

    setProgress('Fetching extra info');
    try {
      const infoRes = await axios.post(
        `/api/sites/${siteId}/fetch-extra-info`,
        {
          name: res.data.siteData.name,
          location:
            res.data.siteData.address ||
            `${res.data.siteData.lat},${res.data.siteData.lng}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInstagramUrl(infoRes.data.instagram_url || '');
      setTwitterUrl(infoRes.data.twitter_url || '');
      setPinterestUrl(infoRes.data.pinterest_url || '');
      setTiktokUrl(infoRes.data.tiktok_url || '');
      setYoutubeUrl(infoRes.data.youtube_url || '');
      setSpotifyUrl(infoRes.data.spotify_url || '');
      setSoundcloudUrl(infoRes.data.soundcloud_url || '');
      setMixcloudUrl(infoRes.data.mixcloud_url || '');
      setAppleMusicUrl(infoRes.data.apple_music_url || '');
      setTripadvisorUrl(infoRes.data.tripadvisor_url || '');
      setMenuUrl(infoRes.data.menu_url || '');
      setLogoUrl(infoRes.data.logo_url || '');
      setBusinessDescription((prev) => {
        const next = String(infoRes.data.business_description || '').trim();
        return next || prev;
      });
      setSocialStats(infoRes.data.social_media_stats || {});
      setFacebookUrl(infoRes.data.facebook_url || '');
    } catch (err) {
      console.error('fetch-extra-info failed', err.response?.data || err.message);
    }

    setProgress('Generating Review Summary');
    try {
      const summaryRes = await axios.post(
        `/api/sites/${siteId}/regenerate-review-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewSummary(summaryRes.data.reviewSummary || '');
      setReviewQuotes(summaryRes.data.reviewQuotes || []);
    } catch (err) {
      console.error('regenerate-review-summary failed', err.response?.data || err.message);
    }

    setProgress('Generating marketing kit');
    try {
      const mkRes = await axios.post(
        `/api/sites/${siteId}/regenerate-marketing-kit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMarketingKit(mkRes.data.marketingKit || '');
    } catch (err) {
      console.error('regenerate-marketing-kit failed', err.response?.data || err.message);
    }

    await loadFromDb();
    setProgress('');
  }, [loadFromDb, placeId, siteId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!editable || !siteId) return;
    if (placeId === 'existing' || placeId === 'manual') return;
    const photos = Array.isArray(editable.photosJson)
      ? editable.photosJson
      : [];
    const unsavedPhotos = photos.filter((p) => !p.saved);
    if (unsavedPhotos.length === 0) return;
    if (autoSaveInProgress.current) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    autoSaveInProgress.current = true;

    const saveAllPhotos = async () => {
      try {
        await axios.post(
          `/api/sites/${siteId}/save-photos`,
          { photos: unsavedPhotos },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          await loadFromDb();
        }
      } catch (err) {
        console.error(
          'auto-save photos error',
          err.response?.data || err.message
        );
      } finally {
        autoSaveInProgress.current = false;
      }
    };

    saveAllPhotos();

    return () => {
      cancelled = true;
    };
  }, [editable, placeId, siteId, load]);

  const searchPlaces = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/places/search', {
        params: { q: query, _ts: Date.now() },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setResults(res.data);
    } catch (err) {
      console.error('places search failed', err.response?.data || err.message);
    }
  };

  const handleSelectPlace = (pid) => {
    setPlaceId(pid);
    setResults([]);
    setQuery('');
  };

  if (!editable) return <div className="p-4">Loading...</div>;

  const openingHoursLines = getOpeningHoursLines(editable.siteData.opening_hours_json);
  const showMapsOnly = mode === 'maps';
  const mapPreviewContainerClass = showMapsOnly ? 'h-[70vh] overflow-auto' : 'max-h-80 overflow-auto';

  const mapsSection = (showHeading = true) => (
    <div className="mt-4 border border-gray-800 rounded p-3 bg-gray-900 text-gray-100">
      <div className="flex items-center justify-between gap-2">
        <div>
          {showHeading && <div className="font-semibold">Site Maps</div>}
          <div className="text-xs text-gray-400">Upload a site map and manage its legend items.</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={mapFileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleMapFileSelected}
          />
          <button
            type="button"
            className="border border-gray-700 px-2 py-1 text-sm whitespace-nowrap rounded bg-gray-800 text-white hover:bg-gray-700"
            onClick={handleUploadMapClick}
          >
            Upload Map
          </button>
        </div>
      </div>

      {mapsLoading && (
        <div className="text-sm text-gray-400 mt-2">Loading maps…</div>
      )}

      {!mapsLoading && siteMaps.length === 0 && (
        <div className="text-sm text-gray-400 mt-2">No maps uploaded yet.</div>
      )}

      {!mapsLoading && siteMaps.length > 0 && (
        <div className="mt-3 space-y-4">
          <label className="block text-sm">
            Select Map
            <select
              className="mt-1 border border-gray-700 w-full p-1 rounded bg-gray-800 text-white"
              value={selectedMapId || ''}
              onChange={(e) => setSelectedMapId(e.target.value || null)}
            >
              {siteMaps.map((map) => (
                <option key={map.id} value={map.id}>
                  {map.name || `Map #${map.id}`}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            Map Name
            <div className="flex gap-2 mt-1">
              <input
                className="border border-gray-700 w-full p-1 rounded bg-gray-800 text-white"
                value={mapNameDraft}
                onChange={(e) => setMapNameDraft(e.target.value)}
                placeholder="e.g. Main temple map"
              />
              <button
                type="button"
                className="border border-gray-700 px-2 py-1 text-sm whitespace-nowrap rounded bg-gray-800 text-white hover:bg-gray-700"
                onClick={handleSaveMapSettings}
              >
                Save Map
              </button>
            </div>
          </label>

          {selectedMapId && (
            <div className="border border-gray-800 rounded bg-gray-950 p-2">
              {(() => {
                const selected = siteMaps.find((map) => String(map.id) === String(selectedMapId));
                if (!selected?.imageUrl) return null;
                return (
                  <div className={mapPreviewContainerClass}>
                    <div className="relative w-full">
                      <img
                        ref={mapPreviewRef}
                        src={selected.imageUrl}
                        alt="Site map preview"
                        className="w-full h-auto block cursor-crosshair"
                        onClick={handleMapClick}
                      />
                      {plaquesOnSelectedMap.filter((plaque) => plaque.map_marker_enabled && plaque.map_marker_x !== null && plaque.map_marker_y !== null).map((plaque) => {
                        const left = `${(plaque.map_marker_x || 0) * 100}%`;
                        const top = `${(plaque.map_marker_y || 0) * 100}%`;
                        const legendNumber = legendNumberById.get(plaque.id);
                        return (
                          <div
                            key={`marker-${plaque.id}`}
                            className="absolute rounded-full bg-black/70 text-white text-xs font-semibold flex items-center justify-center border border-white/70"
                            style={{
                              left,
                              top,
                              transform: 'translate(-50%, -50%)',
                              width: `calc(28px * ${labelScale})`,
                              height: `calc(28px * ${labelScale})`,
                              fontSize: `calc(0.75rem * ${labelScale})`,
                              opacity: labelOpacity,
                            }}
                            title={plaque.title}
                          >
                            {legendNumber || '•'}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {activeMarkerPlaqueId && (
                <div className="text-xs text-gray-400 mt-2">Click the map to set marker position.</div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="text-sm font-semibold">Legend & Markers (Plaque Titles)</div>
            <div className="flex flex-wrap gap-3 text-xs text-gray-300">
              <label className="flex items-center gap-2">
                Label size
                <input
                  type="range"
                  min="0.6"
                  max="2"
                  step="0.05"
                  value={labelScale}
                  onChange={(e) => setLabelScale(Number(e.target.value))}
                />
              </label>
              <label className="flex items-center gap-2">
                Label opacity
                <input
                  type="range"
                  min="0.2"
                  max="1"
                  step="0.05"
                  value={labelOpacity}
                  onChange={(e) => setLabelOpacity(Number(e.target.value))}
                />
              </label>
            </div>
            {mapPlaques.map((plaque) => {
              const assigned = selectedMapId && String(plaque.map_id) === String(selectedMapId);
              return (
                <div key={`map-plaque-${plaque.id}`} className="border border-gray-800 rounded p-2 bg-gray-950 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="font-semibold text-sm">{plaque.title}</div>
                    <label className="text-xs flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={Boolean(assigned)}
                        onChange={async (e) => {
                          const nextAssigned = e.target.checked;
                          try {
                            if (nextAssigned) {
                              await updatePlaqueMapSettings(plaque.id, { mapId: Number(selectedMapId) });
                            } else {
                              await updatePlaqueMapSettings(plaque.id, {
                                mapId: null,
                                mapMarkerEnabled: false,
                                mapIncludeInLegend: false,
                                mapMarkerX: null,
                                mapMarkerY: null,
                                mapLegendOrder: null,
                              });
                            }
                          } catch (err) {
                            console.error('Failed to update plaque map', err);
                            alert('Failed to update plaque map.');
                          }
                        }}
                        disabled={!selectedMapId}
                      />
                      Use on this map
                    </label>
                    <label className="text-xs flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={Boolean(plaque.map_include_in_legend)}
                        onChange={async (e) => {
                          try {
                            await updatePlaqueMapSettings(plaque.id, {
                              mapId: Number(selectedMapId),
                              mapIncludeInLegend: e.target.checked,
                            });
                          } catch (err) {
                            console.error('Failed to update legend inclusion', err);
                            alert('Failed to update legend inclusion.');
                          }
                        }}
                        disabled={!assigned}
                      />
                      Include in legend
                    </label>
                    <label className="text-xs flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={Boolean(plaque.map_marker_enabled)}
                        onChange={async (e) => {
                          try {
                            await updatePlaqueMapSettings(plaque.id, {
                              mapId: Number(selectedMapId),
                              mapMarkerEnabled: e.target.checked,
                            });
                          } catch (err) {
                            console.error('Failed to update marker', err);
                            alert('Failed to update marker.');
                          }
                        }}
                        disabled={!assigned}
                      />
                      Show marker
                    </label>
                    <label className="text-xs flex items-center gap-1">
                      Order
                      <input
                        type="number"
                        className="border border-gray-700 w-16 p-1 text-xs rounded bg-gray-800 text-white"
                        value={plaque.map_legend_order || ''}
                        onChange={async (e) => {
                          try {
                            await updatePlaqueMapSettings(plaque.id, {
                              mapId: Number(selectedMapId),
                              mapLegendOrder: e.target.value ? Number(e.target.value) : null,
                            });
                          } catch (err) {
                            console.error('Failed to update legend order', err);
                            alert('Failed to update legend order.');
                          }
                        }}
                        disabled={!assigned}
                      />
                    </label>
                    <button
                      type="button"
                      className={`border border-gray-700 px-2 py-1 text-xs rounded ${activeMarkerPlaqueId === plaque.id ? 'bg-gray-800 text-white' : 'bg-gray-900 text-gray-200'}`}
                      onClick={() => setActiveMarkerPlaqueId(plaque.id)}
                      disabled={!assigned}
                    >
                      Place marker
                    </button>
                  </div>
                  {assigned && plaque.map_marker_enabled && plaque.map_marker_x !== null && plaque.map_marker_y !== null && (
                    <div className="text-xs text-gray-400">
                      Marker: {Number(plaque.map_marker_x).toFixed(3)}, {Number(plaque.map_marker_y).toFixed(3)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="border border-red-700 px-2 py-1 text-sm rounded text-red-300 hover:bg-red-900/30"
              onClick={handleDeleteMap}
              disabled={!selectedMapId}
            >
              Delete Map
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (showMapsOnly) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Site Maps</h2>
          {onClose && (
            <button
              className="text-sm text-blue-600"
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
        {mapsSection(false)}
      </div>
    );
  }

  const handleChange = (field, value) => {
    setEditable((prev) => ({
      ...prev,
      siteData: { ...prev.siteData, [field]: value },
    }));
  };

  const handleBusinessTypeChange = (value) => {
    const normalized = normalizeBusinessType(value);
    setEditable((prev) => ({
      ...prev,
      siteData: {
        ...prev.siteData,
        business_type: normalized,
        primary_type: normalized,
        primary_type_display_name: value,
      },
    }));
  };

  const handleOpeningHoursChange = (value) => {
    const { openingHoursJson, openingHoursSummary } = openingHoursFromText(value);
    setEditable((prev) => ({
      ...prev,
      siteData: {
        ...prev.siteData,
        opening_hours_json: openingHoursJson,
        opening_hours: openingHoursSummary,
      },
    }));
  };

  const refreshDetails = async () => {
    const token = localStorage.getItem('token');
    await axios.post(
      `/api/sites/${siteId}/refresh-details`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await load();
  };

  const refreshReviews = async () => {
    const token = localStorage.getItem('token');
    await axios.post(
      `/api/sites/${siteId}/refresh-reviews`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await load();
  };

  const regenerateSummary = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `/api/sites/${siteId}/regenerate-review-summary`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setReviewSummary(res.data.reviewSummary || '');
    setReviewQuotes(res.data.reviewQuotes || []);
  };

  const regenerateMarketingKit = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `/api/sites/${siteId}/regenerate-marketing-kit`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setMarketingKit(res.data.marketingKit || '');
  };

  const analyseReviews = async () => {
    if (!siteReviews.length || analysingReviews) return;
    setAnalysingReviews(true);
    setReviewAnalysisMessage('');
    try {
      const token = localStorage.getItem('token');
      const summaryRes = await axios.post(
        `/api/sites/${siteId}/regenerate-review-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewSummary(summaryRes.data.reviewSummary || '');
      setReviewQuotes(summaryRes.data.reviewQuotes || []);

      const marketingRes = await axios.post(
        `/api/sites/${siteId}/regenerate-marketing-kit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMarketingKit(marketingRes.data.marketingKit || '');
      setReviewAnalysisMessage('AI review summary and marketing kit generated.');
    } catch (err) {
      setReviewAnalysisMessage(
        err.response?.data?.error || 'Reviews are saved, but AI analysis could not be completed.'
      );
    } finally {
      setAnalysingReviews(false);
    }
  };

  const exportAllSiteInfo = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const accessibleSites = Array.isArray(res.data?.sites) ? res.data.sites : [];
      if (!accessibleSites.length) {
        alert('No sites available to export.');
        return;
      }
      const filenameDate = new Date().toISOString().slice(0, 10);
      downloadCsvFile(
        `site-info-export-${filenameDate}.csv`,
        SITE_INFO_EXPORT_COLUMNS,
        accessibleSites
      );
    } catch (err) {
      console.error('Failed to export site info', err.response?.data || err.message);
      alert('Failed to export site info.');
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (selectedMapId) {
      try {
        await handleSaveMapSettings();
      } catch (err) {
        console.error('Failed to save map settings', err);
      }
    }
    await axios.post(
      `/api/sites/${siteId}/confirm-business`,
      {
        siteData: editable.siteData,
        reviewsJson: editable.reviewsJson,
        photosJson: editable.photosJson,
        reviewSummary,
        reviewQuotes,
        marketingKit,
        wifi_ssid: wifiSsid || null,
        wifi_password: wifiPass || null,
        contact_email: contactEmail || null,
        facebook_url: facebookUrl || null,
        instagram_url: instagramUrl || null,
        twitter_url: twitterUrl || null,
        pinterest_url: pinterestUrl || null,
        tiktok_url: tiktokUrl || null,
        youtube_url: youtubeUrl || null,
        spotify_url: spotifyUrl || null,
        soundcloud_url: soundcloudUrl || null,
        mixcloud_url: mixcloudUrl || null,
        apple_music_url: appleMusicUrl || null,
        tripadvisor_url: tripadvisorUrl || null,
        google_review_url: googleReviewUrl || null,
        menu_url: menuUrl || null,
        logo_url: logoUrl || null,
        business_description: businessDescription || null,
        social_media_stats: socialStats,
        packages,
        defaultMonetisationMode: monetisationIsEnforced
          ? 'full'
          : normalizeMonetisationMode(defaultMonetisationMode, 'off'),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await axios.put(
      `/api/sites/${siteId}`,
      {
        countryCode: editable.siteData.country_code || null,
        youtubeRegionCode: editable.siteData.youtube_region_code || null,
        destinationSlug: editable.siteData.destination_slug || null,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (refreshSitesAreasDevices) {
      await refreshSitesAreasDevices(siteId, area?.id, true);
    }
    alert('Site information saved!');
    if (onClose) onClose();
  };

  const handleRenameSite = async () => {
    const token = localStorage.getItem('token');
    await axios.put(
      `/api/sites/${siteId}`,
      { name: inputValue },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (refreshSitesAreasDevices)
      await refreshSitesAreasDevices(siteId, undefined, true);
    setAction(null);
    if (onClose) onClose();
  };

  const handleRenameArea = async () => {
    if (!area) return;
    const token = localStorage.getItem('token');
    await axios.put(
      `/api/areas/${area.id}`,
      { name: inputValue },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (refreshSitesAreasDevices)
      await refreshSitesAreasDevices(siteId, area.id, true);
    setAction(null);
    if (onClose) onClose();
  };

  const handleAddArea = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      '/api/areas',
      {
        siteId,
        name: inputValue,
        areaType: isDestinationMode ? 'activity_hub' : 'default',
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (refreshSitesAreasDevices) {
      await refreshSitesAreasDevices(siteId, res.data.areaId, true);
    }
    setAction(null);
    if (onClose) onClose();
  };

  const handleDeleteSite = async () => {
    const token = localStorage.getItem('token');
    await axios.delete(`/api/sites/${siteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    window.location.reload();
  };

  const handleDeleteDestination = async () => {
    const destinationName = String(editable?.siteData?.name || 'this destination').trim();
    const confirmed = window.confirm(
      `Delete destination "${destinationName}"?\n\nThis will permanently delete the destination site and all associated areas/activity hubs. This cannot be undone.`
    );
    if (!confirmed) return;
    await handleDeleteSite();
  };

  const handleDeleteArea = async () => {
    if (!area) return;
    const token = localStorage.getItem('token');
    await axios.delete(`/api/areas/${area.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (refreshSitesAreasDevices)
      await refreshSitesAreasDevices(siteId, undefined, true);
    setAction(null);
    if (onClose) onClose();
  };

  const handleChangeSiteSelect = (pid) => {
    handleSelectPlace(pid);
    setAction(null);
  };

  const handleAddSiteSelect = async (pid) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      '/api/sites',
      { name: 'Untitled Venue' },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newSiteId = res.data.siteId;
    const packageRes = await axios.put(
      `/api/sites/${newSiteId}/package`,
      { packageKey: selectedSitePackageKey },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (packageRes.data?.effectiveSubscriptionType) {
      const subscriptionRes = await axios.put(
        '/api/users/me/subscription',
        { subscriptionType: packageRes.data.effectiveSubscriptionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (subscriptionRes.data?.token) {
        localStorage.setItem('token', subscriptionRes.data.token);
      }
    }
    // Reset local state to avoid mixing with previous site data
    setDraft(null);
    setEditable(null);
    setReviewSummary('');
    setReviewQuotes([]);
    setMarketingKit('');
    setWifiSsid('');
    setWifiPass('');
    setContactEmail('');
    setFacebookUrl('');
    setInstagramUrl('');
    setTwitterUrl('');
    setPinterestUrl('');
    setTripadvisorUrl('');
    setGoogleReviewUrl('');
    setMenuUrl('');
    setLogoUrl('');
    setBusinessDescription('');
    setSocialStats({});
    setPackages([]);

    setSiteId(newSiteId);
    if (pid === 'manual') {
      if (refreshSitesAreasDevices)
        await refreshSitesAreasDevices(newSiteId, undefined, true);
      setPlaceId('manual');
    } else {
      setPlaceId(pid);
    }
    setResults([]);
    setQuery('');
    setAction(null);
  };

  const handleAddDestination = async () => {
    const token = localStorage.getItem('token');
    const trimmedName = String(destinationName || '').trim();
    const normalizedCountryCode = String(destinationCountryCode || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z]/g, '');

    if (!trimmedName) {
      alert('Destination name is required.');
      return;
    }
    if (!normalizedCountryCode || normalizedCountryCode.length !== 2) {
      alert('Country code must be a 2-letter value (for example: vn, nz).');
      return;
    }

    const res = await axios.post(
      '/api/sites',
      {
        name: trimmedName,
        siteType: 'destination',
        countryCode: normalizedCountryCode,
        destinationSlug: trimmedName,
        siteSlug: 'overview',
        hubStatus: 'unclassified',
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const newSiteId = res.data?.siteId;
    const newAreaId = res.data?.areaId;
    if (!newSiteId) {
      alert('Destination was created but no site id was returned.');
      return;
    }

    if (refreshSitesAreasDevices) {
      await refreshSitesAreasDevices(newSiteId, newAreaId, true);
    }

    setSiteId(newSiteId);
    setAction(null);
    setDestinationName('');
    setDestinationCountryCode('');

    if (typeof onOpenDestinationSetup === 'function') {
      onOpenDestinationSetup(newSiteId);
    }
  };

  const isDestinationSite =
    String(editable?.siteData?.site_type || draft?.siteData?.site_type || '')
      .trim()
      .toLowerCase() === 'destination';
  const sharedDestinationSites = Array.isArray(sharedDestinations)
    ? sharedDestinations.filter((entry) => String(entry?.site_type || '').trim().toLowerCase() === 'destination')
    : [];
  const destinationSites = sharedDestinationSites;
  const currentDestinationCountryCode = normalizeDestinationField(editable?.siteData?.country_code);
  const currentDestinationSlug = normalizeDestinationField(editable?.siteData?.destination_slug);
  const selectedLinkedDestination = destinationSites.find((entry) =>
    normalizeDestinationField(entry?.country_code) === currentDestinationCountryCode
      && normalizeDestinationField(entry?.destination_slug) === currentDestinationSlug
  ) || null;
  const linkedDestinationActivityHubs = Array.isArray(selectedLinkedDestination?.areas)
    ? selectedLinkedDestination.areas.filter(
      (entry) => String(entry?.area_type || '').trim().toLowerCase() === 'activity_hub'
    )
    : [];
  const addEntityAction = isDestinationMode ? 'addDestination' : 'addSite';
  const addEntityLabel = isDestinationMode ? 'Add Destination' : 'Add Site';
  const saveEntityLabel = isDestinationMode ? `Save ${siteLabel}` : 'Save Site';
  const changeEntityLabel = isDestinationMode ? `Change ${siteLabel}` : 'Change Site';
  const renameEntityLabel = isDestinationMode ? `Rename ${siteLabel}` : 'Rename Site';
  const addAreaLabel = `Add ${areaLabel}`;
  const renameAreaLabel = `Rename ${areaLabel}`;
  const deleteAreaLabel = `Delete ${areaLabel}`;
  const areaNamePlaceholder = `New ${areaLabel} Name`;
  const searchPlaceholder = isDestinationMode ? 'Destination name' : 'Business name';
  const skipLabel = isDestinationMode ? 'Create without Google import' : 'Skip';
  const sitePhotos = Array.isArray(editable.photosJson) ? editable.photosJson : [];
  const siteReviews = Array.isArray(editable.reviewsJson) ? editable.reviewsJson : [];
  const socialStatEntries = Object.entries(socialStats || {});
  const completedIdentityFields = [
    editable.siteData.name,
    editable.siteData.address,
    editable.siteData.phone,
    contactEmail,
    editable.siteData.website,
    editable.siteData.google_url,
  ].filter((value) => String(value || '').trim()).length;
  const savedLinkCount = [
    facebookUrl,
    instagramUrl,
    twitterUrl,
    pinterestUrl,
    tiktokUrl,
    youtubeUrl,
    spotifyUrl,
    soundcloudUrl,
    mixcloudUrl,
    appleMusicUrl,
    tripadvisorUrl,
    googleReviewUrl,
    menuUrl,
  ].filter((value) => String(value || '').trim()).length;
  const guestAccessCount = [logoUrl, wifiSsid, wifiPass].filter((value) => String(value || '').trim()).length;
  const contentReadinessCount = [
    businessDescription,
    reviewSummary,
    marketingKit,
    packages.length ? 'packages' : '',
  ].filter((value) => String(value || '').trim()).length;
  const googleImportStatus = editable.siteData.place_id ? 'Google-linked' : 'Manual entry';
  const socialSectionSummary = savedLinkCount === 1 ? '1 saved link' : `${savedLinkCount} saved links`;
  const reviewSummaryText = `${sitePhotos.length} photo${sitePhotos.length === 1 ? '' : 's'} / ${siteReviews.length} review${siteReviews.length === 1 ? '' : 's'}`;
  const renderUrlField = ({ label, value, onChange, searchQuery, optional = true, wide = false }) => (
    <label className={joinClassNames('confirm-site-field', wide ? 'lg:col-span-2' : '')}>
      <span className="confirm-site-field-label">{label}{optional ? ' (optional)' : ''}</span>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="url"
          className="confirm-site-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="confirm-site-button confirm-site-button-secondary"
          disabled={!getUrlToOpen(value)}
          onClick={() => handleCheckUrl(value)}
        >
          Check URL
        </button>
        <button
          type="button"
          className="confirm-site-button confirm-site-button-muted"
          onClick={() => openGoogleSearch(searchQuery)}
        >
          Search
        </button>
      </div>
    </label>
  );

  return (
    <div className="confirm-site-shell p-4 space-y-5">
      <div className="confirm-site-hero p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <span className="confirm-site-chip">{googleImportStatus}</span>
              {editable.siteData.name ? <span className="confirm-site-chip">{editable.siteData.name}</span> : null}
              {area?.name ? <span className="confirm-site-chip">{areaLabel}: {area.name}</span> : null}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">{detailTitle}</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                This page is now split into collapsible sections so core details, guest access, public links, and admin tools stay readable instead of landing in one long wall of inputs.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="confirm-site-button" onClick={handleSave}>
              {saveEntityLabel}
            </button>
            <button
              type="button"
              className="confirm-site-button confirm-site-button-secondary"
              onClick={exportAllSiteInfo}
            >
              Export All Site Info
            </button>
            {onClose ? (
              <button
                type="button"
                className="confirm-site-button confirm-site-button-muted"
                onClick={onClose}
              >
                Close
              </button>
            ) : null}
          </div>
        </div>

        {progress ? <div className="confirm-site-progress mt-5">{progress}</div> : null}

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SummaryStat
            label="Identity"
            value={`${completedIdentityFields}/6 ready`}
            helper="Name, address, phone, email, website, maps link"
          />
          <SummaryStat
            label="Guest Access"
            value={`${guestAccessCount}/3 ready`}
            helper="Logo, Wi-Fi SSID, Wi-Fi password"
          />
          <SummaryStat
            label="Public Links"
            value={socialSectionSummary}
            helper="Social platforms, review links, and menu links"
          />
          <SummaryStat
            label="Media"
            value={reviewSummaryText}
            helper="Imported photos and reviews"
          />
        </div>
      </div>

      <DisclosureSection
        title="Identity & Contact"
        description="Core public details guests and downstream tools rely on."
        summary={`${completedIdentityFields}/6 filled`}
        defaultOpen
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Name</span>
            <input
              className="confirm-site-input"
              value={editable.siteData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Phone</span>
            <input
              className="confirm-site-input"
              value={editable.siteData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </label>
          <label className="confirm-site-field lg:col-span-2">
            <span className="confirm-site-field-label">Address</span>
            <input
              className="confirm-site-input"
              value={editable.siteData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Public Contact Email</span>
            <input
              type="email"
              className="confirm-site-input"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="name@example.com"
            />
            <span className="confirm-site-field-hint">
              This is the email field that was missing from the page. Website templates already know how to use it.
            </span>
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Default Monetisation Mode</span>
            <select
              className="confirm-site-select"
              value={monetisationIsEnforced ? 'full' : defaultMonetisationMode}
              onChange={(e) => setDefaultMonetisationMode(e.target.value)}
              disabled={monetisationIsEnforced}
            >
              {MONETISATION_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="confirm-site-field-hint">
              {monetisationIsEnforced
                ? 'Free-tier sites always use Full monetisation.'
                : 'Used when a plaque does not have its own monetisation override.'}
            </span>
          </label>
          {renderUrlField({
            label: 'Website',
            value: editable.siteData.website || '',
            onChange: (value) => handleChange('website', value),
            searchQuery: `${getBusinessQueryBase()} website`,
            optional: false,
          })}
          {renderUrlField({
            label: 'Google Maps URL',
            value: editable.siteData.google_url || '',
            onChange: (value) => handleChange('google_url', value),
            searchQuery: getBusinessQueryBase(),
          })}
        </div>

        {!isDestinationSite ? (
          <div className="confirm-site-note mt-5 space-y-4">
            <div>
              <div className="text-base font-semibold text-white">Destination Mapping</div>
              <div className="mt-1 text-sm text-slate-400">
                Link this venue to an admin-managed destination so info plaques resolve to the correct activity pages.
              </div>
            </div>

            {destinationSites.length > 0 ? (
              <label className="confirm-site-field">
                <span className="confirm-site-field-label">Linked Destination</span>
                <select
                  className="confirm-site-select"
                  value={selectedLinkedDestination?.id || ''}
                  onChange={(e) => {
                    const nextId = String(e.target.value || '').trim();
                    const nextDestination = destinationSites.find(
                      (entry) => String(entry?.id) === nextId
                    ) || null;
                    handleChange('country_code', nextDestination?.country_code || '');
                    handleChange('destination_slug', nextDestination?.destination_slug || '');
                  }}
                >
                  <option value="">No linked destination</option>
                  {destinationSites.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                      {entry.country_code || entry.destination_slug
                        ? ` (${String(entry.country_code || '').toUpperCase()} / ${entry.destination_slug || ''})`
                        : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                No admin-managed destinations are available yet.
                {isAdmin && typeof onOpenDestinationSetup === 'function' ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => onOpenDestinationSetup()}
                    >
                      Open Destination Setup
                    </button>
                    {' '}to create one first.
                  </>
                ) : null}
              </div>
            )}

            {selectedLinkedDestination ? (
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-sm text-emerald-50">
                <div className="font-semibold text-emerald-100">
                  Linked to: {selectedLinkedDestination.name}
                </div>
                <div className="mt-2">Destination slug: {selectedLinkedDestination.destination_slug || 'not set'}</div>
                <div>Country code: {String(selectedLinkedDestination.country_code || '').toUpperCase() || 'not set'}</div>
                <div>Activity hubs found: {linkedDestinationActivityHubs.length}</div>
                {linkedDestinationActivityHubs.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {linkedDestinationActivityHubs.map((hub) => (
                      <span
                        key={hub.id}
                        className="rounded-full bg-emerald-950/70 px-3 py-1 text-xs font-semibold text-emerald-50"
                      >
                        {hub.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-emerald-100/80">
                    No activity hubs are currently attached to this destination site.
                  </div>
                )}
              </div>
            ) : currentDestinationCountryCode || currentDestinationSlug ? (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                The saved destination mapping no longer matches a current destination in this account. Pick one from the list above or keep the manual values below.
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <label className="confirm-site-field">
                <span className="confirm-site-field-label">Country Code (ISO-2)</span>
                <input
                  className="confirm-site-input"
                  value={editable.siteData.country_code || ''}
                  onChange={(e) => handleChange('country_code', e.target.value)}
                  placeholder="e.g. vn"
                />
              </label>
              <label className="confirm-site-field">
                <span className="confirm-site-field-label">Destination Slug</span>
                <input
                  className="confirm-site-input"
                  value={editable.siteData.destination_slug || ''}
                  onChange={(e) => handleChange('destination_slug', e.target.value)}
                  placeholder="e.g. danang"
                />
              </label>
            </div>
          </div>
        ) : null}
      </DisclosureSection>

      <DisclosureSection
        title="Business & Imported Data"
        description="Business details can be imported from a provider or entered manually."
        summary={googleImportStatus}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Place ID</span>
            <input
              className="confirm-site-input"
              value={editable.siteData.place_id || ''}
              onChange={(e) => handleChange('place_id', e.target.value)}
              placeholder="Optional"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">YouTube Region Code</span>
            <input
              className="confirm-site-input"
              value={editable.siteData.youtube_region_code || ''}
              onChange={(e) => handleChange('youtube_region_code', e.target.value.toUpperCase())}
              placeholder="e.g. US"
              maxLength={2}
            />
            <span className="confirm-site-field-hint">
              Used by fallback playlist checks for regional availability.
            </span>
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Business Type</span>
            <input
              className="confirm-site-input"
              value={
                editable.siteData.primary_type_display_name ||
                humanizeBusinessType(editable.siteData.primary_type) ||
                humanizeBusinessType(editable.siteData.business_type) ||
                ''
              }
              onChange={(e) => handleBusinessTypeChange(e.target.value)}
              placeholder="e.g. Restaurant or Live Music Bar"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Public Rating</span>
            <input
              type="number"
              className="confirm-site-input"
              min="0"
              max="5"
              step="0.1"
              value={editable.siteData.rating ?? ''}
              onChange={(e) => handleChange('rating', e.target.value)}
              placeholder="0–5"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Public Review Count</span>
            <input
              type="number"
              className="confirm-site-input"
              min="0"
              step="1"
              value={editable.siteData.ratings_total ?? ''}
              onChange={(e) => handleChange('ratings_total', e.target.value)}
              placeholder="0"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">UTC Offset Minutes</span>
            <input
              type="number"
              className="confirm-site-input"
              value={editable.siteData.utc_offset_minutes ?? ''}
              min="-840"
              max="840"
              step="15"
              onChange={(e) => handleChange('utc_offset_minutes', e.target.value)}
              placeholder="e.g. 420"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Business Status</span>
            <select
              className="confirm-site-input"
              value={editable.siteData.business_status || ''}
              onChange={(e) => handleChange('business_status', e.target.value)}
            >
              <option value="">Not specified</option>
              <option value="OPERATIONAL">Operational</option>
              <option value="CLOSED_TEMPORARILY">Temporarily closed</option>
              <option value="CLOSED_PERMANENTLY">Permanently closed</option>
            </select>
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Latitude</span>
            <input
              type="number"
              className="confirm-site-input"
              min="-90"
              max="90"
              step="any"
              value={editable.siteData.lat ?? ''}
              onChange={(e) => handleChange('lat', e.target.value)}
              placeholder="e.g. 15.9142"
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Longitude</span>
            <input
              type="number"
              className="confirm-site-input"
              min="-180"
              max="180"
              step="any"
              value={editable.siteData.lng ?? ''}
              onChange={(e) => handleChange('lng', e.target.value)}
              placeholder="e.g. 108.3398"
            />
          </label>
          <label className="confirm-site-field lg:col-span-2">
            <span className="confirm-site-field-label">Regular Hours of Operation</span>
            <textarea
              className="confirm-site-textarea font-mono text-sm"
              rows={Math.max(3, Math.min(10, openingHoursLines.length || 3))}
              value={openingHoursLines.join('\n')}
              onChange={(e) => handleOpeningHoursChange(e.target.value)}
              placeholder={'Monday: 9:00 AM – 10:00 PM\nTuesday: 9:00 AM – 10:00 PM'}
            />
          </label>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" className="confirm-site-button confirm-site-button-secondary" onClick={refreshDetails}>
            Refresh Details
          </button>
          <button type="button" className="confirm-site-button confirm-site-button-muted" onClick={refreshReviews}>
            Refresh Reviews
          </button>
        </div>
      </DisclosureSection>

      <DisclosureSection
        title="Public Links & Social Profiles"
        description="Links for guests, QR experiences, and website modules."
        summary={socialSectionSummary}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {renderUrlField({
            label: 'Facebook Page URL',
            value: facebookUrl,
            onChange: setFacebookUrl,
            searchQuery: `${getBusinessQueryBase()} facebook`,
          })}
          {renderUrlField({
            label: 'Instagram URL',
            value: instagramUrl,
            onChange: setInstagramUrl,
            searchQuery: `${getBusinessQueryBase()} instagram`,
            optional: false,
          })}
          {renderUrlField({
            label: 'Twitter URL',
            value: twitterUrl,
            onChange: setTwitterUrl,
            searchQuery: `${getBusinessQueryBase()} twitter`,
          })}
          {renderUrlField({
            label: 'Pinterest URL',
            value: pinterestUrl,
            onChange: setPinterestUrl,
            searchQuery: `${getBusinessQueryBase()} pinterest`,
          })}
          {renderUrlField({
            label: 'TikTok URL',
            value: tiktokUrl,
            onChange: setTiktokUrl,
            searchQuery: `${getBusinessQueryBase()} tiktok`,
          })}
          {renderUrlField({
            label: 'YouTube URL',
            value: youtubeUrl,
            onChange: setYoutubeUrl,
            searchQuery: `${getBusinessQueryBase()} youtube`,
          })}
          {renderUrlField({
            label: 'Spotify URL',
            value: spotifyUrl,
            onChange: setSpotifyUrl,
            searchQuery: `${getBusinessQueryBase()} spotify`,
          })}
          {renderUrlField({
            label: 'SoundCloud URL',
            value: soundcloudUrl,
            onChange: setSoundcloudUrl,
            searchQuery: `${getBusinessQueryBase()} soundcloud`,
          })}
          {renderUrlField({
            label: 'Mixcloud URL',
            value: mixcloudUrl,
            onChange: setMixcloudUrl,
            searchQuery: `${getBusinessQueryBase()} mixcloud`,
          })}
          {renderUrlField({
            label: 'Apple Music URL',
            value: appleMusicUrl,
            onChange: setAppleMusicUrl,
            searchQuery: `${getBusinessQueryBase()} apple music`,
          })}
          {renderUrlField({
            label: 'TripAdvisor URL',
            value: tripadvisorUrl,
            onChange: setTripadvisorUrl,
            searchQuery: `${getBusinessQueryBase()} tripadvisor`,
          })}
          {renderUrlField({
            label: 'Google Review URL',
            value: googleReviewUrl,
            onChange: setGoogleReviewUrl,
            searchQuery: `${getBusinessQueryBase()} reviews`,
          })}
          {renderUrlField({
            label: 'Menu URL',
            value: menuUrl,
            onChange: setMenuUrl,
            searchQuery: `${getBusinessQueryBase()} menu`,
          })}
        </div>

        {socialStatEntries.length > 0 ? (
          <div className="confirm-site-note mt-5">
            <div className="text-base font-semibold text-white">Imported Social Media Stats</div>
            <div className="mt-3 grid gap-4 lg:grid-cols-2">
              {socialStatEntries.map(([platform, metrics]) => (
                <div key={platform} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                  <div className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-300">
                    {platform}
                  </div>
                  <div className="mt-3 space-y-3">
                    {Object.entries(metrics || {}).map(([key, value]) => (
                      <label key={key} className="confirm-site-field">
                        <span className="confirm-site-field-label">{key}</span>
                        <input
                          type="text"
                          className="confirm-site-input"
                          value={value}
                          onChange={(e) =>
                            setSocialStats((prev) => ({
                              ...prev,
                              [platform]: { ...prev[platform], [key]: e.target.value },
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </DisclosureSection>

      <DisclosureSection
        title="Branding & Guest Access"
        description="Wi-Fi handoff and logo assets used across menus, pages, and QR experiences."
        summary={`${guestAccessCount}/3 ready`}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Wi-Fi SSID</span>
            <input
              className="confirm-site-input"
              value={wifiSsid}
              onChange={(e) => setWifiSsid(e.target.value)}
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Wi-Fi Password</span>
            <input
              className="confirm-site-input"
              value={wifiPass}
              onChange={(e) => setWifiPass(e.target.value)}
            />
          </label>
          <div className="confirm-site-field lg:col-span-2">
            <span className="confirm-site-field-label">Logo</span>
            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoFileSelected}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="confirm-site-button confirm-site-button-secondary"
                onClick={handleUploadLogoClick}
              >
                Upload Logo
              </button>
              {getUrlToOpen(logoUrl) ? (
                <button
                  type="button"
                  className="confirm-site-button confirm-site-button-muted"
                  onClick={() => handleCheckUrl(logoUrl)}
                >
                  View Current Logo
                </button>
              ) : null}
            </div>
            {getUrlToOpen(logoUrl) ? (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white p-4">
                <img
                  src={getUrlToOpen(logoUrl)}
                  alt="Logo preview"
                  className="max-h-24 object-contain"
                />
              </div>
            ) : (
              <div className="mt-3 rounded-2xl border border-dashed border-white/10 bg-slate-950/50 p-4 text-sm text-slate-400">
                No logo uploaded yet.
              </div>
            )}
          </div>
        </div>
      </DisclosureSection>

      <DisclosureSection
        title="Content, Messaging & Packages"
        description="Long-form copy and structured offer blocks used in websites and marketing surfaces."
        summary={`${contentReadinessCount}/4 ready`}
      >
        <div className="grid grid-cols-1 gap-4">
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Business Description</span>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              className="confirm-site-textarea"
              rows={4}
            />
          </label>
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Review Summary</span>
            <textarea
              className="confirm-site-textarea"
              rows={5}
              value={reviewSummary}
              onChange={(e) => setReviewSummary(e.target.value)}
            />
          </label>
          {reviewQuotes.length > 0 ? (
            <div className="confirm-site-note">
              <div className="text-base font-semibold text-white">Review Quotes</div>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-300">
                {reviewQuotes.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <label className="confirm-site-field">
            <span className="confirm-site-field-label">Marketing Kit</span>
            <textarea
              className="confirm-site-textarea"
              rows={7}
              value={marketingKit}
              onChange={(e) => setMarketingKit(e.target.value)}
            />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" className="confirm-site-button confirm-site-button-secondary" onClick={regenerateSummary}>
            Regenerate Review Summary
          </button>
          <button type="button" className="confirm-site-button confirm-site-button-muted" onClick={regenerateMarketingKit}>
            Regenerate Marketing Kit
          </button>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-white">Packages / Menu Items</div>
              <div className="mt-1 text-sm text-slate-400">
                Structured offers that can be reused by website modules and menu-like experiences.
              </div>
            </div>
            <button
              type="button"
              className="confirm-site-button"
              onClick={() =>
                setPackages([
                  ...packages,
                  { title: '', description: '', price: '', url: '' },
                ])
              }
            >
              Add Package
            </button>
          </div>

          {packages.length > 0 ? (
            <div className="mt-4 space-y-4">
              {packages.map((pkg, idx) => (
                <div key={idx} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <label className="confirm-site-field">
                      <span className="confirm-site-field-label">Title</span>
                      <input
                        type="text"
                        value={pkg.title || ''}
                        onChange={(e) => {
                          const newPkgs = [...packages];
                          newPkgs[idx].title = e.target.value;
                          setPackages(newPkgs);
                        }}
                        className="confirm-site-input"
                      />
                    </label>
                    <label className="confirm-site-field">
                      <span className="confirm-site-field-label">Price</span>
                      <input
                        type="text"
                        value={pkg.price || ''}
                        onChange={(e) => {
                          const newPkgs = [...packages];
                          newPkgs[idx].price = e.target.value;
                          setPackages(newPkgs);
                        }}
                        className="confirm-site-input"
                      />
                    </label>
                    <label className="confirm-site-field lg:col-span-2">
                      <span className="confirm-site-field-label">Description</span>
                      <textarea
                        value={pkg.description || ''}
                        onChange={(e) => {
                          const newPkgs = [...packages];
                          newPkgs[idx].description = e.target.value;
                          setPackages(newPkgs);
                        }}
                        className="confirm-site-textarea"
                      />
                    </label>
                    <label className="confirm-site-field lg:col-span-2">
                      <span className="confirm-site-field-label">URL</span>
                      <input
                        type="url"
                        value={pkg.url || ''}
                        onChange={(e) => {
                          const newPkgs = [...packages];
                          newPkgs[idx].url = e.target.value;
                          setPackages(newPkgs);
                        }}
                        className="confirm-site-input"
                      />
                    </label>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      className="confirm-site-button confirm-site-button-danger"
                      onClick={() => {
                        const newPkgs = packages.filter((_, i) => i !== idx);
                        setPackages(newPkgs);
                      }}
                    >
                      Remove Package
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-slate-950/50 p-4 text-sm text-slate-400">
              No packages added yet.
            </div>
          )}
        </div>
      </DisclosureSection>

      <DisclosureSection
        title="Photos & Reviews"
        description="Imported media and guest feedback kept separate from the editing-heavy copy fields."
        summary={reviewSummaryText}
      >
        {sitePhotos.length > 0 ? (
          <div>
            <div className="text-base font-semibold text-white">Photos</div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
              {sitePhotos.map((p) => (
                <div key={p.name} className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60">
                  <PlacePhoto name={p.name} maxWidth={320} alt="" className="h-36 w-full object-cover" />
                  {p.saved ? (
                    <span className="absolute left-3 top-3 rounded-full bg-emerald-600 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-white">
                      Saved
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/50 p-4 text-sm text-slate-400">
            No imported photos yet.
          </div>
        )}

        <div className="mt-6">
          <div className="text-base font-semibold text-white">Reviews</div>
          <div className="mt-4">
            <PasteReviews
              siteId={siteId}
              onImported={(data) => {
                setEditable((prev) => ({
                  ...prev,
                  reviewsJson: Array.isArray(data.reviewsJson) ? data.reviewsJson : prev.reviewsJson,
                }));
                setReviewSummary(data.reviewSummary || '');
                setReviewQuotes(Array.isArray(data.reviewQuotes) ? data.reviewQuotes : []);
                setMarketingKit(data.marketingKit || '');
              }}
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="confirm-site-button confirm-site-button-secondary"
              onClick={analyseReviews}
              disabled={!siteReviews.length || analysingReviews}
            >
              {analysingReviews ? 'Analysing reviews…' : 'Analyse reviews with AI'}
            </button>
            {reviewAnalysisMessage ? (
              <span className="text-sm text-slate-300">{reviewAnalysisMessage}</span>
            ) : null}
          </div>
          {reviewSummary || marketingKit ? (
            <div className="confirm-site-note mt-4 space-y-4">
              <div className="text-base font-semibold text-white">AI Review Analysis</div>
              {reviewSummary ? (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Review Summary
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-200">
                    {reviewSummary}
                  </p>
                </div>
              ) : null}
              {marketingKit ? (
                <details>
                  <summary className="cursor-pointer text-sm font-semibold text-slate-200">
                    View generated marketing kit
                  </summary>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-300">
                    {marketingKit}
                  </p>
                </details>
              ) : null}
            </div>
          ) : null}
          {siteReviews.length > 0 ? (
            <div className="mt-4 max-h-80 space-y-3 overflow-y-auto pr-1">
              {siteReviews.map((r, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                  <p className="text-sm leading-6 text-slate-200">
                    {r.text?.text || r.originalText || ''}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-slate-950/50 p-4 text-sm text-slate-400">
              No imported reviews yet.
            </div>
          )}
        </div>
      </DisclosureSection>

      <DisclosureSection
        title="Admin Tools"
        description="Site management, area management, switching records, and destructive actions are tucked away here."
        summary="Manage structure"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="confirm-site-button confirm-site-button-secondary"
              onClick={() => setAction('changeSite')}
            >
              {changeEntityLabel}
            </button>
            {(addEntityAction === 'addSite' || isAdmin) ? (
              <button
                type="button"
                className="confirm-site-button confirm-site-button-muted"
                onClick={() => {
                  if (addEntityAction === 'addDestination') {
                    setDestinationName('');
                    setDestinationCountryCode('');
                  } else {
                    setSelectedSitePackageKey(subscriptionType || 'standard');
                    setPackageCatalogError('');
                  }
                  setAction(addEntityAction);
                }}
              >
                {addEntityLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="confirm-site-button confirm-site-button-muted"
              onClick={() => {
                setInputValue(editable.siteData.name || '');
                setAction('renameSite');
              }}
            >
              {renameEntityLabel}
            </button>
            <button
              type="button"
              className="confirm-site-button confirm-site-button-muted"
              onClick={() => {
                setInputValue('');
                setAction('addArea');
              }}
            >
              {addAreaLabel}
            </button>
            {area ? (
              <button
                type="button"
                className="confirm-site-button confirm-site-button-muted"
                onClick={() => {
                  setInputValue(area.name || '');
                  setAction('renameArea');
                }}
              >
                {renameAreaLabel}
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {!isDestinationSite ? (
              <button
                type="button"
                className="confirm-site-button confirm-site-button-danger"
                onClick={handleDeleteSite}
              >
                Delete Site
              </button>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                className={joinClassNames(
                  'confirm-site-button',
                  isDestinationSite ? 'confirm-site-button-danger' : 'confirm-site-button-muted'
                )}
                onClick={handleDeleteDestination}
                disabled={!isDestinationSite}
                title={
                  isDestinationSite
                    ? 'Delete this destination and all associated activity hubs/areas'
                    : 'Delete Destination is only available when the selected site is a destination'
                }
              >
                Delete Destination
              </button>
            ) : null}
            {area ? (
              <button
                type="button"
                className="confirm-site-button confirm-site-button-danger"
                onClick={handleDeleteArea}
              >
                {deleteAreaLabel}
              </button>
            ) : null}
          </div>

          {action === 'renameSite' ? (
            <div className="confirm-site-note flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                className="confirm-site-input flex-1"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button type="button" className="confirm-site-button" onClick={handleRenameSite}>
                Save
              </button>
            </div>
          ) : null}

          {action === 'renameArea' ? (
            <div className="confirm-site-note flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                className="confirm-site-input flex-1"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button type="button" className="confirm-site-button" onClick={handleRenameArea}>
                Save
              </button>
            </div>
          ) : null}

          {action === 'addArea' ? (
            <div className="confirm-site-note flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                className="confirm-site-input flex-1"
                placeholder={areaNamePlaceholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button type="button" className="confirm-site-button" onClick={handleAddArea}>
                Add
              </button>
            </div>
          ) : null}

          {(action === 'changeSite' || action === 'addSite') ? (
            <div className="confirm-site-note space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  className="confirm-site-input flex-1"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button type="button" className="confirm-site-button" onClick={searchPlaces}>
                  Search
                </button>
              </div>
              {!isDestinationMode ? (
                <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">Choose package for this site</div>
                      <div className="mt-1 text-xs text-slate-400">
                        This sets the commercial package for the new site before you finish setup.
                      </div>
                    </div>
                    <div className="rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                      VND
                    </div>
                  </div>
                  {packageCatalogError ? (
                    <div className="mb-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
                      {packageCatalogError}
                    </div>
                  ) : null}
                  <div className="grid gap-3 lg:grid-cols-2">
                    {packageCards.map((pkg) => {
                      const isSelected = selectedSitePackageKey === pkg.key;
                      return (
                        <button
                          key={pkg.key}
                          type="button"
                          className="rounded-2xl border px-4 py-3 text-left transition"
                          style={{
                            borderColor: isSelected ? 'rgba(99,102,241,0.75)' : 'rgba(148,163,184,0.25)',
                            background: isSelected ? 'rgba(99,102,241,0.12)' : 'rgba(15,23,42,0.55)',
                          }}
                          onClick={() => setSelectedSitePackageKey(pkg.key)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                {pkg.label}
                              </div>
                              <div className="mt-1 text-lg font-bold text-white">
                                {pkg.isPoa ? 'POA' : `${formatVnd(pkg.basePrice)} VND`}
                              </div>
                            </div>
                            <div className="text-xs font-semibold text-slate-300">
                              {isSelected ? 'Selected' : 'Select'}
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-slate-300">{pkg.summary}</div>
                          <div className="mt-2 text-xs text-slate-400">
                            {summarizeIncludedFeatures(pkg) || 'No included items configured'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <ul className="max-h-48 overflow-y-auto rounded-2xl border border-white/10 bg-slate-950/70 p-2">
                {results.map((r) => (
                  <li
                    key={r.place_id}
                    className="cursor-pointer rounded-xl px-3 py-2 text-sm text-slate-200 transition hover:bg-white/5"
                    onClick={() =>
                      action === 'addSite'
                        ? handleAddSiteSelect(r.place_id)
                        : handleChangeSiteSelect(r.place_id)
                    }
                  >
                    {r.description}
                  </li>
                ))}
              </ul>
              {action === 'addSite' ? (
                <button
                  type="button"
                  className="confirm-site-button confirm-site-button-muted w-full"
                  onClick={() => handleAddSiteSelect('manual')}
                >
                  {skipLabel}
                </button>
              ) : null}
            </div>
          ) : null}

          {isAdmin && action === 'addDestination' ? (
            <div className="confirm-site-note space-y-3">
              <input
                type="text"
                className="confirm-site-input"
                placeholder="Destination name (e.g. Da Nang)"
                value={destinationName}
                onChange={(e) => setDestinationName(e.target.value)}
              />
              <input
                type="text"
                className="confirm-site-input"
                placeholder="Country code (ISO-2, e.g. vn)"
                value={destinationCountryCode}
                onChange={(e) => setDestinationCountryCode(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <button type="button" className="confirm-site-button" onClick={handleAddDestination}>
                  Create Destination
                </button>
                <button
                  type="button"
                  className="confirm-site-button confirm-site-button-muted"
                  onClick={() => setAction(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </DisclosureSection>
    </div>
  );
}
